const DISABLED_CLASSES = [
  "swiper-button-disabled",
  "infoblock-swiper-button-disabled",
];

const READY_CLASS = "swiper-initialized";
const ROOT_SELECTOR =
  ".js-info-block-slider, .reader-news-swiper-container, .partners-news-swiper-container, .info-block-swiper-container";
const NAV_SELECTOR =
  ".js-info-block-next, .js-info-block-prev, .swiper-button-next-unique, .swiper-button-prev-unique, [class*='swiper-button-next'], [class*='swiper-button-prev']";
const INTERACTIVE_SELECTOR =
  "a[href], button, input, select, textarea, label, summary, [role='button'], [role='link'], [contenteditable='true']";
const DRAGGING_CLASS = "is-scroll-snap-dragging";
const SETTLING_CLASS = "is-scroll-snap-settling";
const DRAG_THRESHOLD = 4;
const INTERACTIVE_DRAG_THRESHOLD = 12;
const DRAG_ADVANCE_THRESHOLD = 40;
const DRAG_ADVANCE_RATIO = 0.15;
const CLICK_SUPPRESSION_MS = 150;
const SETTLING_FALLBACK_MS = 300;

const getWrapper = (slider) => slider.querySelector(".swiper-wrapper");

const getSlides = (slider) => {
  const wrapper = getWrapper(slider);

  return wrapper ? Array.from(wrapper.children) : [];
};

const getSlideOffset = (slider, slide) => {
  const wrapper = getWrapper(slider);

  return slide.offsetLeft - (wrapper ? wrapper.offsetLeft : 0);
};

const getNearestSlideIndex = (slider, slides) => {
  const currentLeft = slider.scrollLeft;

  return slides.reduce(
    (nearest, slide, index) => {
      const distance = Math.abs(getSlideOffset(slider, slide) - currentLeft);

      return distance < nearest.distance ? { distance, index } : nearest;
    },
    { distance: Number.POSITIVE_INFINITY, index: 0 }
  ).index;
};

const getSlideStep = (slider, slides, index) => {
  const currentSlide = slides[index];
  const nextSlide = slides[index + 1];
  const prevSlide = slides[index - 1];

  if (currentSlide && nextSlide) {
    return getSlideOffset(slider, nextSlide) - getSlideOffset(slider, currentSlide);
  }

  if (currentSlide && prevSlide) {
    return getSlideOffset(slider, currentSlide) - getSlideOffset(slider, prevSlide);
  }

  return slider.clientWidth;
};

const setDisabled = (button, disabled) => {
  DISABLED_CLASSES.forEach((className) => {
    button.classList.toggle(className, disabled);
  });

  button.setAttribute("aria-disabled", disabled ? "true" : "false");

  if (button.tagName === "BUTTON") {
    button.disabled = disabled;
  } else if (disabled) {
    button.setAttribute("tabindex", "-1");
  } else {
    button.setAttribute("role", "button");
    button.setAttribute("tabindex", "0");
  }
};

const findNavigation = (slider) => {
  const root = slider.closest(ROOT_SELECTOR) || slider.parentElement || document;
  const buttons = Array.from(root.querySelectorAll(NAV_SELECTOR));

  return {
    nextButton: buttons.find((button) =>
      Array.from(button.classList).some((className) => className.includes("next"))
    ),
    prevButton: buttons.find((button) =>
      Array.from(button.classList).some((className) => className.includes("prev"))
    ),
  };
};

const updateNavigationState = (slider, navigation) => {
  const hasOverflow = slider.scrollWidth > slider.clientWidth + 1;
  const isAtStart = slider.scrollLeft <= 1;
  const isAtEnd = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1;

  if (navigation.prevButton) {
    setDisabled(navigation.prevButton, !hasOverflow || isAtStart);
  }

  if (navigation.nextButton) {
    setDisabled(navigation.nextButton, !hasOverflow || isAtEnd);
  }
};

const scrollToSlide = (slider, direction) => {
  const slides = getSlides(slider);

  if (slides.length < 2) {
    return;
  }

  const currentIndex = getNearestSlideIndex(slider, slides);
  const nextIndex =
    direction === "next"
      ? Math.min(currentIndex + 1, slides.length - 1)
      : Math.max(currentIndex - 1, 0);

  slider.scrollTo({
    left: getSlideOffset(slider, slides[nextIndex]),
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
  });
};

const scrollToSlideIndex = (slider, slides, index) => {
  slider.scrollTo({
    left: getSlideOffset(slider, slides[index]),
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
  });
};

const scrollToNearestSlide = (slider) => {
  const slides = getSlides(slider);

  if (slides.length < 2) {
    return;
  }

  const nearestIndex = getNearestSlideIndex(slider, slides);

  scrollToSlideIndex(slider, slides, nearestIndex);
};

const scrollToDragTarget = (slider, startIndex, startScrollLeft, dragDistance) => {
  const slides = getSlides(slider);

  if (slides.length < 2) {
    return;
  }

  const slideStep = Math.max(Math.abs(getSlideStep(slider, slides, startIndex)), 1);
  const draggedScrollDistance = slider.scrollLeft - startScrollLeft;
  const advanceThreshold = Math.max(
    DRAG_ADVANCE_THRESHOLD,
    slideStep * DRAG_ADVANCE_RATIO
  );

  if (Math.abs(draggedScrollDistance) < advanceThreshold) {
    scrollToNearestSlide(slider);
    return;
  }

  const movedSlides = Math.round(draggedScrollDistance / slideStep);
  const directionalStep = dragDistance < 0 ? 1 : -1;
  const targetDelta =
    movedSlides === 0 || Math.sign(movedSlides) !== Math.sign(directionalStep)
      ? directionalStep
      : movedSlides;
  const nextIndex = Math.max(
    0,
    Math.min(slides.length - 1, startIndex + targetDelta)
  );

  scrollToSlideIndex(slider, slides, nextIndex);
};

const settleAfterDrag = (slider, callback) => {
  let timeoutId = null;

  const finish = () => {
    window.clearTimeout(timeoutId);
    slider.removeEventListener("scrollend", finish);
    slider.classList.remove(SETTLING_CLASS);
  };

  slider.classList.add(SETTLING_CLASS);
  slider.addEventListener("scrollend", finish, { once: true });

  window.requestAnimationFrame(() => {
    callback();

    timeoutId = window.setTimeout(
      finish,
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? 0
        : SETTLING_FALLBACK_MS
    );
  });
};

const bindActivation = (button, callback) => {
  button.addEventListener("click", (event) => {
    if (button.getAttribute("aria-disabled") === "true") {
      event.preventDefault();
      return;
    }

    callback(event);
  });

  if (button.tagName !== "BUTTON") {
    button.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      button.click();
    });
  }
};

const bindDragScroll = (slider) => {
  let pointerId = null;
  let startX = 0;
  let startScrollLeft = 0;
  let startIndex = 0;
  let dragDistance = 0;
  let didDrag = false;
  let suppressClick = false;
  let startedOnInteractiveElement = false;

  slider.addEventListener("pointerdown", (event) => {
    if (
      event.button !== 0 ||
      event.pointerType === "touch" ||
      event.target.closest(NAV_SELECTOR) ||
      slider.scrollWidth <= slider.clientWidth + 1
    ) {
      return;
    }

    pointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = slider.scrollLeft;
    startIndex = getNearestSlideIndex(slider, getSlides(slider));
    dragDistance = 0;
    didDrag = false;
    startedOnInteractiveElement = Boolean(event.target.closest(INTERACTIVE_SELECTOR));
  });

  slider.addEventListener("pointermove", (event) => {
    if (event.pointerId !== pointerId) {
      return;
    }

    dragDistance = event.clientX - startX;

    const dragThreshold = startedOnInteractiveElement
      ? INTERACTIVE_DRAG_THRESHOLD
      : DRAG_THRESHOLD;

    if (!didDrag && Math.abs(dragDistance) > dragThreshold) {
      didDrag = true;
      slider.setPointerCapture(pointerId);
      slider.classList.add(DRAGGING_CLASS);
    }

    if (!didDrag) {
      return;
    }

    event.preventDefault();
    slider.scrollLeft = startScrollLeft - dragDistance;
  });

  const stopDragging = (event) => {
    if (event.pointerId !== pointerId) {
      return;
    }

    if (slider.hasPointerCapture(pointerId)) {
      slider.releasePointerCapture(pointerId);
    }

    pointerId = null;
    slider.classList.remove(DRAGGING_CLASS);

    if (!didDrag) {
      return;
    }

    suppressClick = true;
    window.setTimeout(() => {
      suppressClick = false;
    }, CLICK_SUPPRESSION_MS);

    settleAfterDrag(slider, () => {
      scrollToDragTarget(slider, startIndex, startScrollLeft, dragDistance);
    });
  };

  slider.addEventListener("pointerup", stopDragging);
  slider.addEventListener("pointercancel", stopDragging);
  slider.addEventListener("dragstart", (event) => event.preventDefault());

  slider.addEventListener(
    "click",
    (event) => {
      if (!suppressClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    },
    true
  );
};

const initScrollSnapSlider = (slider) => {
  if (slider.dataset.scrollSnapState === "ready") {
    return;
  }

  const slides = getSlides(slider);

  if (!slides.length) {
    return;
  }

  const navigation = findNavigation(slider);
  let pendingFrame = null;

  slider.dataset.scrollSnapState = "ready";
  slider.classList.add(READY_CLASS, "is-scroll-snap-ready");

  const scheduleNavigationUpdate = () => {
    if (pendingFrame) {
      return;
    }

    pendingFrame = window.requestAnimationFrame(() => {
      pendingFrame = null;
      updateNavigationState(slider, navigation);
    });
  };

  if (navigation.nextButton) {
    bindActivation(navigation.nextButton, () => scrollToSlide(slider, "next"));
  }

  if (navigation.prevButton) {
    bindActivation(navigation.prevButton, () => scrollToSlide(slider, "prev"));
  }

  bindDragScroll(slider);

  slider.addEventListener("scroll", scheduleNavigationUpdate, { passive: true });
  window.addEventListener("resize", scheduleNavigationUpdate);
  updateNavigationState(slider, navigation);
};

const initScrollSnapSliders = () => {
  document.querySelectorAll(".swiper").forEach(initScrollSnapSlider);
};

const initTrackedInfoBlocks = () => {
  document.querySelectorAll("[data-track-view-id]").forEach((block) => {
    if (block.dataset.trackState === "sent") {
      return;
    }

    block.dataset.trackState = "sent";

    fetch("/api/block/statistic/add", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        data: {
          block_id: Number(block.dataset.trackViewId),
          type: "view",
        },
      }),
    }).catch(() => {
      block.dataset.trackState = "failed";
    });
  });
};

const initFrameRotators = () => {
  document.querySelectorAll("[data-frame-rotator]").forEach((rotator) => {
    if (rotator.dataset.rotatorState === "started") {
      return;
    }

    const frames = rotator.querySelectorAll("iframe");
    if (frames.length < 2) {
      return;
    }

    rotator.dataset.rotatorState = "started";
    let currentIndex = 0;
    const interval = Number(rotator.dataset.interval || 8000);

    window.setInterval(() => {
      const nextIndex = (currentIndex + 1) % frames.length;

      frames[currentIndex].style.opacity = "0";
      frames[currentIndex].style.zIndex = "1";

      frames[nextIndex].style.opacity = "1";
      frames[nextIndex].style.zIndex = "2";

      currentIndex = nextIndex;
    }, interval);
  });
};

const init = () => {
  initScrollSnapSliders();
  initTrackedInfoBlocks();
  initFrameRotators();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
